/*
  Combining catalog rows that share a set name (D30).

  A game's own set list really does carry the same `setName` more than once, and it is
  usually meaningful: for Pokémon, six of the eight duplicated names are two language
  editions of one set — English and Japanese, English and Korean, Japanese and Chinese.

  It is not, however, reachable. The route is `/:gameCode/:setName` and the set page
  fetches `v1/gameInfo/{gameCode}/set/{setName}`, so BOTH rows resolve to one page whose
  population is the two added together — verified against production on four pairs, the
  page total equalling the sum every time (Detective Pikachu 22 + 4 = 26). Two cards
  bearing the same name and the same link, each showing part of the number the page will
  show, is the worst of the three available states.

  So the rows are combined: one card per name, the counts added so the figure matches the
  page it links to, and the languages listed underneath so the variants are still visible.
  D29 does the same thing to the search dropdown for the same reason.

  What this deliberately does NOT do is claim a language the data does not have. Three of
  the eight pairs have no `language` on one side, and 280 of Pokémon's 800 sets have none
  at all, so the list is of what is *known* and is simply absent when nothing is.
*/

export interface CombinableSet {
  setName: string;
  cardsGraded: number | null;
  /* One row's own language, as the v2 catalog gives it. */
  language?: string | null;
  /* Every language this card now stands for, in first-seen order. */
  languages?: string[];
}

/*
  Keyed on the trimmed name, because that is what the cell renders and what the route
  carries — production has a Pokémon set named " S&V: Stellar Crystal", leading space and
  all, and it must not be a different card from an untrimmed twin.

  First row wins for identity (id, image, release date): it is the one the sort order
  already put first, and the two rows describe the same set.

  `cardsGraded` stays null only when EVERY row is null. A null means "never graded" and
  filterCardsGraded drops those, so turning a genuine null into 0 would put never-graded
  sets back into surfaces that deliberately exclude them.
*/
export type CombinedSet<T> = T & { languages: string[] };

export const combineSetsByName = <T extends CombinableSet>(
  sets: readonly T[]
): CombinedSet<T>[] => {
  const byName = new Map<string, CombinedSet<T>>();

  sets.forEach((set) => {
    const key = set.setName.trim();
    const existing = byName.get(key);

    const languages = (set.languages ?? []).slice();
    if (set.language && !languages.includes(set.language)) {
      languages.push(set.language);
    }

    if (!existing) {
      byName.set(key, { ...set, languages });
      return;
    }

    const merged = (existing.languages ?? []).slice();
    languages.forEach((language) => {
      if (!merged.includes(language)) {
        merged.push(language);
      }
    });

    const total =
      existing.cardsGraded === null && set.cardsGraded === null
        ? null
        : (existing.cardsGraded ?? 0) + (set.cardsGraded ?? 0);

    byName.set(key, { ...existing, cardsGraded: total, languages: merged });
  });

  return Array.from(byName.values());
};
